import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Flex,
  Skeleton,
  Text,
} from '@kognitos/lattice';

import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import { useRetrieveWorkspaceBook } from '@/generated/hooks/bdk/v2/services/library';
import type { V2BookConnectionDescriptor } from '@/generated/hooks/bdk/models';
import { ConnectionStatusTag } from '@/modules/integrations/components/ConnectionStatusTag';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import {
  getInitials,
  getIntegrationDisplayName,
} from '@/shared/utils/integrations-utils';

/** One integration in the rail: the book's real icon, its display name, and
 *  its connection state. Mirrors the drafts integrations panel rather than
 *  inventing a second treatment — same `useRetrieveWorkspaceBook` call and
 *  params, so it is normally a cache hit, and the same `ConnectionStatusTag`.
 *
 *  Its own component because the icon needs a query per connection, which a
 *  `.map()` in the parent cannot do. */
export function RailIntegrationCard({
  connection,
  descriptor,
}: {
  connection: V1CredentialInfo;
  descriptor?: V2BookConnectionDescriptor;
}) {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data, isLoading } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    connection.book ?? '',
    connection.book_version ?? '',
    {
      query: {
        enabled: Boolean(
          organizationId &&
            workspaceId &&
            connection.book &&
            connection.book_version,
        ),
      },
    },
  );

  const book = data?.data.book;
  const imageSrc = convertToDataUrl(book?.icon);
  const displayName = book
    ? getIntegrationDisplayName(book)
    : (connection.book ?? '');
  // The connection's own name lives on the workspace descriptor, not on the
  // manifest credential. Without a resolved descriptor the code alias is the
  // best name we have — it is what the automation itself calls this
  // connection, and `credentialsByAlias` guarantees it is non-empty.
  const connectionName =
    descriptor?.connection_name || connection.connection_alias || '';
  const showConnectionName =
    connectionName.toLowerCase() !== displayName.toLowerCase();
  // Keyed on the connection, not the book, so two connections to one book
  // don't share test ids.
  const testIdKey =
    descriptor?.connection_id || connection.connection_id || connection.book;

  return (
    <Flex
      align="center"
      gap={2}
      className="rounded-xl border bg-layout px-3 py-3"
      data-testid={`draft-rail-integration-${testIdKey}`}
    >
      {isLoading ? (
        <Skeleton className="size-8 shrink-0 rounded-lg" />
      ) : (
        <Avatar className="size-8 shrink-0 rounded-lg">
          {imageSrc && (
            <AvatarImage
              src={imageSrc}
              alt={displayName}
              className="object-contain"
            />
          )}
          <AvatarFallback className="rounded-lg bg-muted text-[10px] font-medium text-muted-foreground">
            {getInitials(displayName)}
          </AvatarFallback>
        </Avatar>
      )}
      {/* Integration above, connection below: the book is the category and
       * the connection is the thing chosen within it. Both truncate — a rail
       * is 320px wide and connection names are user-typed — and reveal
       * themselves on hover only when they actually clip.
       *
       * `align="stretch"` is what makes that truncation happen at all. On
       * lattice's `items-start` default each line sizes to its own text and
       * overflows this column instead of being clipped by it, so a long
       * connection name runs straight across the status tag. */}
      <Flex
        direction="column"
        align="stretch"
        gap={0}
        className="min-w-0 flex-1"
      >
        <Text
          as="span"
          level="xSmall"
          color="muted"
          ellipsis={{ tooltip: true }}
          data-testid={`draft-rail-integration-name-${testIdKey}`}
        >
          {displayName}
        </Text>
        {showConnectionName ? (
          <Text
            as="span"
            level="small"
            ellipsis={{ tooltip: true }}
            data-testid={`draft-rail-connection-name-${testIdKey}`}
          >
            {connectionName}
          </Text>
        ) : null}
      </Flex>
      {/* The status is the one thing here that must stay whole: it is short,
       * and a squeezed "Inactive" is worse than a shorter name. */}
      <Flex
        align="center"
        className="shrink-0"
        data-testid={`draft-rail-integration-status-${testIdKey}`}
      >
        <ConnectionStatusTag
          connections={descriptor ? [descriptor] : []}
          status={descriptor?.status?.state}
          style="icon"
        />
      </Flex>
    </Flex>
  );
}
